import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
} from "@mui/material";
import {
  ReceiptLong,
  AttachMoney,
  TrendingDown,
  TrendingUp,
  Add,
} from "@mui/icons-material";
import axios from "axios";
import apiEndpoints from "../../apiconfig";

const formatCurrency = (amt) =>
  "₹ " + Number(amt || 0).toLocaleString("en-IN");

const Overview = ({ fromDate, toDate, openCreateInvoice }) => {
  const [summary, setSummary] = useState({
    student_fee: 0,
    staff_salary: 0,
    expense: 0,
    profit: 0,
  });

  const [studentFees, setStudentFees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [staffSalaries, setStaffSalaries] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [filterFromDate, setFilterFromDate] = useState(fromDate || "");
  const [filterToDate, setFilterToDate] = useState(toDate || "");

  const token = localStorage.getItem("token");

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return dateStr.split(" ")[0]; // removes time part
  };

  /* ---------------- FETCH REPORT DATA ---------------- */
  useEffect(() => {

    const fetchReport = async () => {
      try {
        setLoading(true);
        const res = await axios.get(apiEndpoints.reports_financial, {
          params: {
            from_date: filterFromDate,
            to_date: filterToDate,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (res.data.status === "success") {
          setSummary(res.data.summary);
          setStudentFees(res.data.data.student_fees || []);
          setStaffSalaries(res.data.data.staff_salaries || []);
          setExpenses(res.data.data.expenses || []);

        }
      } catch (err) {
        console.error("Report fetch error", err);
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [filterFromDate, filterToDate, token]);

  /* ---------------- SUMMARY VALUES ---------------- */
  const {
    student_fee,
    staff_salary,
    expense,
    profit,
  } = summary;

  return (
    <Grid container spacing={3}>
      {/* ---------------- STAT CARDS ---------------- */}

      {/* Student Fee */}
      <Grid item xs={12} sm={6} md={3}>
        <Paper sx={cardStyle}>
          <Box sx={iconRow}>
            <ReceiptLong color="primary" />
            <Typography variant="subtitle2">Student Fee</Typography>
          </Box>
          <Typography variant="h5" fontWeight={700}>
            {formatCurrency(student_fee)}
          </Typography>
        </Paper>
      </Grid>

      {/* Staff Salary */}
      <Grid item xs={12} sm={6} md={3}>
        <Paper sx={cardStyle}>
          <Box sx={iconRow}>
            <TrendingDown color="error" />
            <Typography variant="subtitle2">Staff Salary</Typography>
          </Box>
          <Typography variant="h5" fontWeight={700} color="error.main">
            {formatCurrency(staff_salary)}
          </Typography>
        </Paper>
      </Grid>

      {/* Expense */}
      <Grid item xs={12} sm={6} md={3}>
        <Paper sx={cardStyle}>
          <Box sx={iconRow}>
            <TrendingDown color="warning" />
            <Typography variant="subtitle2">Expenses</Typography>
          </Box>
          <Typography variant="h5" fontWeight={700} color="warning.main">
            {formatCurrency(expense)}
          </Typography>
        </Paper>
      </Grid>

      {/* Profit */}
      <Grid item xs={12} sm={6} md={3}>
        <Paper sx={cardStyle}>
          <Box sx={iconRow}>
            <TrendingUp color={profit >= 0 ? "success" : "error"} />
            <Typography variant="subtitle2">Profit</Typography>
          </Box>
          <Typography
            variant="h5"
            fontWeight={700}
            color={profit >= 0 ? "success.main" : "error.main"}
          >
            {formatCurrency(profit)}
          </Typography>
        </Paper>
      </Grid>

      <Grid item xs={12}>
        <Paper sx={cardStyle}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="date"
                label="From Date"
                InputLabelProps={{ shrink: true }}
                value={filterFromDate}
                onChange={(e) => setFilterFromDate(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="date"
                label="To Date"
                InputLabelProps={{ shrink: true }}
                value={filterToDate}
                onChange={(e) => setFilterToDate(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <Button
                fullWidth
                variant="contained"
                sx={{ height: "56px", borderRadius: 999 }}
                onClick={() => {
                  // triggers useEffect automatically
                }}
              >
                Apply Filter
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Grid>

      {/* ---------------- STUDENT FEES TABLE ---------------- */}
      <Grid item xs={12} md={12}>
        <Paper sx={cardStyle}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h6" fontWeight={700}>
              Student Fee Transactions
            </Typography>
            <Chip label={`${studentFees.length} records`} size="small" />
          </Box>

          <TableContainer sx={scrollTable}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Transaction ID</TableCell>
                  <TableCell>Student GUID</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Mode</TableCell>
                  <TableCell>Date</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {studentFees.map((row, i) => (
                  <TableRow key={i} hover>
                    <TableCell>{row.transcation_id}</TableCell>
                    <TableCell>{row.student_name}</TableCell>
                    <TableCell align="right">
                      {formatCurrency(row.amount)}
                    </TableCell>
                    <TableCell>{row.mode === "bank" ? "Bank Transfer" : row.mode}</TableCell>
                    <TableCell>{formatDate(row.payment_date)}</TableCell>
                  </TableRow>
                ))}

                {!loading && studentFees.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography color="text.secondary">
                        No data available
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Grid>
      <Grid item xs={12} md={12}>
        <Paper sx={cardStyle}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h6" fontWeight={700}>
              Staff Salary
            </Typography>
            <Chip label={`${staffSalaries.length} records`} size="small" />
          </Box>

          <TableContainer sx={scrollTable}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Invoice</TableCell>
                  <TableCell>Staff</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Mode</TableCell>
                  <TableCell>Date</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {staffSalaries.map((row, i) => (
                  <TableRow key={i} hover>
                    <TableCell>{row.invoice_number}</TableCell>
                    <TableCell>{row.staff_name}</TableCell>
                    <TableCell align="right">
                      {formatCurrency(row.amount)}
                    </TableCell>
                    <TableCell>{row.payment_mode === "bank" ? "Bank Transfer" : row.payment_mode}</TableCell>
                    <TableCell>{row.payment_date}</TableCell>
                  </TableRow>
                ))}

                {!loading && staffSalaries.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography color="text.secondary">
                        No salary data
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Grid>
      <Grid item xs={12}>
        <Paper sx={cardStyle}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h6" fontWeight={700}>
              Expenses
            </Typography>
            <Chip label={`${expenses.length} records`} size="small" />
          </Box>

          <TableContainer sx={scrollTable}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Invoice</TableCell>
                  <TableCell>Expense</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Mode</TableCell>
                  <TableCell>Date</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {expenses.map((row, i) => (
                  <TableRow key={i} hover>
                    <TableCell>{row.invoice_number}</TableCell>
                    <TableCell>{row.expense_name}</TableCell>
                    <TableCell>{row.description}</TableCell>
                    <TableCell align="right">
                      {formatCurrency(row.amount)}
                    </TableCell>
                    <TableCell>{row.payment_mode === "bank" ? "Bank Transfer" : row.payment_mode}</TableCell>
                    <TableCell>{row.payment_date}</TableCell>
                  </TableRow>
                ))}

                {!loading && expenses.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography color="text.secondary">
                        No expense data
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Grid>
    </Grid>
  );
};

/* ---------------- STYLES ---------------- */
const cardStyle = {
  p: 3,
  borderRadius: 3,
  background: "rgba(255,255,255,0.25)",
  backdropFilter: "blur(10px)",
  border: "1px solid rgba(255,255,255,0.4)",
  boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
};

const iconRow = {
  display: "flex",
  alignItems: "center",
  gap: 2,
};

const scrollTable = {
  maxHeight: 360,
  overflowY: "auto",
  scrollbarWidth: "none", // Firefox
  "&::-webkit-scrollbar": {
    display: "none", // Chrome / Safari
  },
};

export default Overview;
